// What Peek adds to the issue's parts: its discussion and its proposal's
// checks and review, with each review session the queue started for it and
// how it ended (Poulpe-495), read-only, from what the store already holds;
// and its sessions, each with its agent's last reply, from its transcript,
// and its actions.
import { LiveChecks, useLiveChecks } from '../checks/progress'
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { errorMessage } from '../api/client'
import type { Issue, Proposal, ReviewOutcome, Session } from '../api/types'
import { uuid4 } from '../api/uuid'
import { ResumeButton } from '../code/ResumeButton'
import { Section, useMounted } from '../components/layout'
import { needsServer } from '../components/needsServer'
import { toast } from '../components/toasts'
import { Time } from '../components/Time'
import { Markdown } from '../markdown/Markdown'
import { useSessionPath } from '../projects/ProjectRoute'
import { proposalPath, short, stepName } from '../queue/words'
import { useAttempt } from '../sessions/attempt'
import { useTranscript } from '../sessions/context'
import { sessionsOf, stateName } from '../sessions/words'
import { useBoardState, useStore } from '../store/context'
import { issueProposal, recordOf } from '../store/store'
import type { Item } from '../transcript/types'
import { actorName } from '../vocab'
import { reviewEndText, reviewsOf } from './stuck'

/** The comments Peek shows, the newest ones. */
export const PEEK_COMMENTS = 20
/** The sessions Peek lists, the newest ones. */
export const PEEK_SESSIONS = 8
/** A reply is cut to this many characters. */
const REPLY_LENGTH = 400

const cut = (text: string, length: number) => (text.length > length ? `${text.slice(0, length).trimEnd()}…` : text)

/** The issue's comments (the record's discussion), oldest first so the newest is last, and a link to the page for the rest. */
export function PeekDiscussion({ issue }: { issue: Issue }) {
  const { entries } = useBoardState()
  const all = useMemo(() => recordOf(entries, issue.id).filter(entry => entry.kind === 'discussion'), [entries, issue.id])
  const shown = all.slice(-PEEK_COMMENTS)
  const page = `/${encodeURIComponent(issue.key)}`
  return <Section title="Discussion" count={all.length}>
    {shown.length === 0 ? <p className="muted">No comments yet.</p> : <ol className="record-list peek-comments">
      {shown.map(entry => <li key={entry.id} className="record-entry" data-record-id={entry.id}>
        <div className="record-head">
          <span className="record-author">{actorName(entry.author)}</span>
          <Time iso={entry.at} className="record-time" />
        </div>
        <Markdown source={cut(entry.body, 4000)} />
      </li>)}
    </ol>}
    {all.length > shown.length && <p className="muted">{all.length - shown.length} earlier. <Link to={`${page}#entry-${all[0]?.number ?? 1}`}>Read them on the issue</Link></p>}
    {all.length > 0 && all.length <= shown.length && <p className="muted"><Link to={page}>Comment on the issue</Link></p>}
  </Section>
}

/**
 * The issue's sessions, newest first: agent, state, the agent's last reply, and the session's actions. Each row follows
 * its session's transcript as the Code tab does (`useTranscript`), sharing its follower when the Code tab holds one: a
 * socket for each shown session that has not ended, and one read for each that has. An issue with three sessions, one of
 * them ended, opens two sockets and reads the third once.
 */
export function PeekSessions({ issue }: { issue: Issue }) {
  const { sessions, offline, projects } = useBoardState()
  const pathOf = useSessionPath()
  const list = useMemo(() => sessionsOf(sessions, issue.id), [sessions, issue.id])
  const shown = list.slice(0, PEEK_SESSIONS)
  return <Section title="Sessions" count={list.length}>
    {offline && <p className="muted peek-offline">Offline: shown as last read.</p>}
    {shown.length === 0 ? <p className="muted">No sessions yet.</p> : <ul className="session-list peek-sessions">
      {shown.map(session => <PeekSession key={session.id} session={session}
        path={pathOf(session.id)} review={session.origin.kind === 'review' ? proposalPath(session.origin.proposal, projects.get(issue.project)?.key) : null} />)}
    </ul>}
    {list.length > shown.length && <p className="muted">{list.length - shown.length} older sessions on the issue.</p>}
  </Section>
}

/**
 * The agent's last reply: what it said since the last prompt, as `get_session`'s `last_reply` gives it, its messages
 * between calls a paragraph each; while a turn runs, what it has said so far. Null when it has said nothing since.
 */
export function replyOf(items: readonly Item[]): string | null {
  let from = 0
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i]!.kind === 'user') { from = i + 1; break }
  }
  const said = items.slice(from).flatMap(item => item.kind === 'assistant' && item.text.trim() ? [item.text.trim()] : [])
  return said.length ? cut(said.join('\n\n'), REPLY_LENGTH) : null
}

/** The session page's question before End (code/CodePage.tsx). */
const END_QUESTION = 'End the session? It takes no more prompts once ended.'

function PeekSession({ session, path, review }: { session: Session; path: string; review: string | null }) {
  const store = useStore()
  const { offline } = useBoardState()
  const view = useTranscript(session)
  const reply = useMemo(() => replyOf(view.items), [view.items])
  const mounted = useMounted()
  const attempt = useAttempt()
  const fieldId = useId()
  const continueButton = useRef<HTMLButtonElement>(null)
  const field = useRef<HTMLInputElement>(null)
  const [asking, setAsking] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [ending, setEnding] = useState(false)
  const name = actorName({ kind: 'agent', agent: session.agent, session: session.id })
  const ended = session.state === 'ended'
  // As the session page's composer (code/Composer.tsx): its runner refuses a prompt until the settings are chosen again.
  const settingsBlocked = !!session.pending_place || session.settings?.change?.state === 'refused' || session.settings?.change?.state === 'unconfirmed'
  useEffect(() => { if (ended) { setAsking(false); setEnding(false) } }, [ended])

  const close = () => {
    setAsking(false)
    setText('')
    continueButton.current?.focus()
  }
  const send = async (event: FormEvent) => {
    event.preventDefault()
    const prompt = text.trim()
    if (sending || offline || settingsBlocked || !prompt) return
    setSending(true)
    const { mutationId } = attempt.ids(JSON.stringify([session.id, prompt]))
    try {
      await store.sendSessionOp({ mutation_id: mutationId, op: 'prompt_session', args: { session_id: session.id, prompt } })
      attempt.applied()
      toast(`Sent to ${name}.`)
      if (mounted.current) close()
    } catch (err) {
      // The words stay, to send again.
      toast(`Could not send to ${name}: ${errorMessage(err)}`, 'error')
      if (mounted.current) field.current?.focus()
    }
    if (mounted.current) setSending(false)
  }
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || sending) return
    event.preventDefault()
    event.stopPropagation()
    close()
  }
  const end = () => {
    if (ending || store.getState().offline) return
    if (!window.confirm(END_QUESTION)) return
    setEnding(true)
    // It ends once its runner says so: the row says Ended, and its actions go.
    store.sendSessionOp({ mutation_id: uuid4(), op: 'stop_session', args: { session_id: session.id } }).catch((err: unknown) => {
      toast(`Could not end the session: ${errorMessage(err)}`, 'error')
      if (mounted.current) setEnding(false)
    })
  }

  return <li className="session-list-item peek-session" data-vendor={session.agent.vendor}>
    <p className="session-row">
      <span className="session-row-agent">{name}</span>
      <span className={`session-state is-${session.state}`}>{stateName(session.state)}</span>
      <Time iso={session.created_at} className="session-row-time" />
      {review && <Link className="tag session-row-origin" to={review}>Review of a proposal</Link>}
      <Link className="button button-small" to={path} aria-label={`Open ${name}'s session`}>Open</Link>
      {!ended && <>
        {/* Not while sending: closing would drop the words that a failed send keeps. */}
        <button ref={continueButton} type="button" className="button button-small" aria-expanded={asking} aria-controls={asking ? fieldId : undefined}
          disabled={sending} onClick={() => (asking ? close() : setAsking(true))}>Continue…</button>
        <button type="button" className="button button-small button-quiet" onClick={end} aria-disabled={ending || offline || undefined} {...needsServer(offline)}>
          {ending ? 'Ending…' : 'End'}
        </button>
      </>}
      {ended && session.resumable && <ResumeButton session={session} />}
    </p>
    {asking && <form className="peek-session-ask" onSubmit={(event) => void send(event)} onKeyDown={onKeyDown}>
      <label className="visually-hidden" htmlFor={fieldId}>{`What to tell ${name}`}</label>
      <input ref={field} id={fieldId} type="text" value={text} onChange={(event) => setText(event.target.value)} readOnly={sending}
        placeholder="Continue the session…" autoFocus autoComplete="off" />
      <button type="button" className="button button-small button-quiet" onClick={close} disabled={sending}>Cancel</button>
      <button type="submit" className="button button-small button-primary" disabled={sending || offline || settingsBlocked || !text.trim()} {...needsServer(offline)}>Send</button>
      {settingsBlocked && <p className="muted" role="status">It takes no prompt until its settings are chosen again: <Link to={path}>open the session</Link>.</p>}
    </form>}
    {reply ? <blockquote className="peek-reply">{reply}</blockquote>
      : view.status === 'loading' ? <p className="muted">Reading the reply…</p>
      : view.status === 'error' ? <p className="muted">Could not read the reply: {view.error?.message ?? 'unknown error'}</p>
      : <p className="muted">No reply yet.</p>}
  </li>
}

/** One check, as the proposal's own fields say it. */
export interface CheckRow { name: string; state: 'waiting' | 'running' | 'passed' | 'failed' | 'not_run'; detail: string | null; at: string | null }

const CHECK_STATE: Record<CheckRow['state'], string> = { waiting: 'Waiting', running: 'Running', passed: 'Passed', failed: 'Failed', not_run: 'Not run' }

/** What the store holds of a proposal's targeted check: its step, its job and why it went back. No check records a time of its own yet. */
export function checksOf(proposal: Proposal): CheckRow[] {
  const name = 'Targeted check'
  const ended = proposal.ended
  if (ended?.kind === 'check_failed') return [{ name, state: 'failed', detail: ended.parts.length ? `Failed: ${ended.parts.join(', ')}` : null, at: null }]
  if (ended?.kind === 'check_not_run') return [{ name, state: 'not_run', detail: cut(ended.message, 300), at: null }]
  if (proposal.step === 'head') return [{ name, state: 'waiting', detail: null, at: null }]
  if (proposal.step === 'check') return [{ name, state: proposal.check_job ? 'running' : 'waiting', detail: null, at: null }]
  if (proposal.step === null) return []
  return [{ name, state: 'passed', detail: null, at: null }]
}

const VERDICTS: Record<ReviewOutcome, string> = { land: 'Land', land_with_changes: 'Land with changes', rework: 'Rework' }

/**
 * Where the proposal's review stands, as its own fields and its review
 * sessions say: waiting for a reviewer and why, under way, given, or not
 * given. None before its check has passed, while no review began.
 */
export function reviewRow(proposal: Proposal, reviews: readonly Session[]): CheckRow | null {
  const name = 'Review'
  if (proposal.review) {
    const { outcome } = proposal.review.verdict
    return { name, state: outcome === 'rework' ? 'failed' : 'passed', detail: `${actorName(proposal.review.reviewer)}: ${VERDICTS[outcome] ?? outcome}`, at: null }
  }
  if (proposal.ended?.kind === 'review_not_given') return { name, state: 'not_run', detail: 'Its review sessions ended without a verdict.', at: null }
  if (proposal.step === 'review') {
    if (proposal.review_waits) return { name, state: 'waiting', detail: `Waits: ${cut(proposal.review_waits, 300)}`, at: null }
    const current = reviews.find(review => review.id === proposal.review_session)
    return { name, state: current && current.state !== 'ended' ? 'running' : 'waiting', detail: null, at: null }
  }
  return null
}

/**
 * The issue's proposal and its live checks, including its candidate. With no
 * open proposal, its newest one if it came back, so that a review not given
 * still shows its review sessions and how they ended (Poulpe-495).
 */
export function PeekProposal({ issue }: { issue: Issue }) {
  const { proposals, returned, landings, projects, offline, sessions } = useBoardState()
  const progress = useLiveChecks()
  const pathOf = useSessionPath()
  const back = [...returned.values()].find(p => p.issue_id === issue.id)
  const found = issueProposal(proposals, landings, issue.id) ?? (back ? { proposal: back, landing: null } : null)
  if (!found) {
    return <Section title="Proposal"><p className="muted">Checks run when an agent proposes a change.</p></Section>
  }
  const { proposal } = found
  const active = progress.find(check => check.proposal === proposal.id)
  const checks = checksOf(proposal).map(check => active ? { ...check, state: active.progress ? 'running' as const : 'waiting' as const } : check)
  const reviews = reviewsOf(proposal, sessions.values())
  const review = reviewRow(proposal, reviews)
  return <Section title="Proposal">
    {offline && <p className="muted peek-offline">Offline: shown as last read.</p>}
    <p>
      <Link to={proposalPath(proposal.id, projects.get(proposal.project_id)?.key)}><code>{short(proposal.head)}</code></Link>
      {' '}<span className="tag peek-proposal-state">{proposal.state === 'open' ? stepName(proposal.step) : proposal.state.charAt(0).toUpperCase() + proposal.state.slice(1)}</span>
      {' '}<Time iso={proposal.created_at} className="peek-proposal-time" />
    </p>
    {found.landing && <LiveChecks landing={found.landing.id} />}
    {checks.length === 0 && !review ? <p className="muted">No check ran.</p> : <ul className="peek-checks" aria-label="Checks">
      {checks.map(check => <li key={check.name} className="peek-check" data-state={check.state}>
        <strong>{check.name}</strong> <span className="tag">{CHECK_STATE[check.state]}</span>
        {check.at && <> <Time iso={check.at} /></>}
        {check.detail && <p>{check.detail}</p>}
        <div className="peek-check-output" data-check-output={check.name}><LiveChecks proposal={proposal.id} /></div>
      </li>)}
      {review && <li className="peek-check" data-state={review.state} data-review>
        <strong>{review.name}</strong> <span className="tag">{CHECK_STATE[review.state]}</span>
        {review.detail && <p>{review.detail}</p>}
        {reviews.length > 0 && <ol className="peek-sessions" aria-label="Review sessions">
          {reviews.map((session, n) => <ReviewAttempt key={session.id} session={session} n={n + 1} path={pathOf(session.id)} />)}
        </ol>}
      </li>}
    </ul>}
  </Section>
}

/** One review session of the proposal: its agent, its state, and how it ended and why, its limits' stop included. */
function ReviewAttempt({ session, n, path }: { session: Session; n: number; path: string }) {
  const name = actorName({ kind: 'agent', agent: session.agent, session: session.id })
  const how = reviewEndText(session)
  return <li data-review-session={session.id} data-vendor={session.agent.vendor}>
    <p className="session-row">
      <span className="session-row-agent">Review {n} · {name}</span>
      <span className={`session-state is-${session.state}`}>{stateName(session.state)}</span>
      <Time iso={session.created_at} className="session-row-time" />
      <Link className="button button-small" to={path} aria-label={`Open review ${n}'s session`}>Open</Link>
    </p>
    {how && <p className="muted">{cut(how, 400)}</p>}
  </li>
}
